import { IUser } from 'src/services/UserService';
import * as actionTypes from '../constants';
import { ICompleteLoanApplicationPayload } from 'src/services/LendingService';
import { AirtableLoanApplicationRecordsResponse } from 'src/services/AirtableService';
import { message } from 'antd';
import { ICreditCheckResponseData } from 'src/lib/types/creditCheck';

const initState = {
  loading: false,
  loanError: true,
  lendingRequests: {},
  agentLoanApplications: {},
  airtableAgentLoanApplications: {},
  creditCheck: {},
  pbRequests: {},
  loans: {},
  updating: false,
  updated: false,
  reports: {
    loading: false,
  },
};

type ILoanSection = {
  count: number;
  data: Array<ICompleteLoanApplicationPayload>;
  has_more: boolean;
  limit: number;
  page: number | string;
  total_count: number;
  error: any;
  loading: boolean;
};

export interface ILendingStore {
  loading: boolean;
  loanError: boolean;
  lendingRequests: ILoanSection;
  agentLoanApplications: ILoanSection;
  airtableAgentLoanApplications: { loading: boolean; error: any; data: AirtableLoanApplicationRecordsResponse; updateLoading?: boolean };
  creditCheck: {
    loading: boolean;
    error: any;
    data: ICreditCheckResponseData['data'];
  };
  pbRequests: ILoanSection;
  loans: ILoanSection;
  user: IUser;
  updateError: any;
  updating: boolean;
  updated: boolean;
  reports: {
    loading: boolean;
  };
}

const lendingReducer = (state: ILendingStore = initState as ILendingStore, action: any): ILendingStore => {
  switch (action.type) {
    case actionTypes.LENDING_REQUEST_PENDING:
      return {
        ...state,
        lendingRequests: { ...state.lendingRequests },
        loading: true,
        loanError: false,
      };
    case actionTypes.LENDING_REQUEST_SUCCESS:
      return {
        ...state,
        loading: false,
        lendingRequests: action.payload,
        loanError: false,
      };
    case actionTypes.LENDING_REQUEST_ERROR:
      return {
        ...state,
        loading: false,
        lendingRequests: {} as any,
        loanError: true,
      };
    case actionTypes.PB_REQUEST_PENDING:
      return {
        ...state,
        loading: true,
        pbRequests: {},
        pbError: false,
      } as any;
    case actionTypes.PB_REQUEST_SUCCESS:
      return {
        ...state,
        loading: false,
        pbRequests: action.payload,
        pbError: false,
      } as any;
    case actionTypes.PB_REQUEST_ERROR:
      return {
        ...state,
        loading: false,
        pbRequests: {},
        pbError: true,
      } as any;
    case actionTypes.PB_REQUEST_ACTION_PENDING:
      return {
        ...state,
        updating: true,
        updated: false,
        updateError: false,
      } as ILendingStore;
    case actionTypes.PB_REQUEST_ACTION_SUCCESS:
      return {
        ...state,
        updating: false,
        updated: true,
        updateError: false,
      } as ILendingStore;
    case actionTypes.PB_REQUEST_ACTION_ERROR:
      return {
        ...state,
        updating: false,
        updated: false,
        updateError: true,
      } as ILendingStore;
    case actionTypes.LOAN_REQUEST_PENDING:
      return {
        ...state,
        loading: true,
        loans: {},
        nplError: false,
      } as any;
    case actionTypes.LOAN_REQUEST_SUCCESS:
      return {
        ...state,
        loading: false,
        loans: action.payload,
        nplError: false,
      } as ILendingStore;
    case actionTypes.LOAN_REQUEST_ERROR:
      return {
        ...state,
        loading: false,
        loans: {},
        nplError: true,
      } as any;
    case actionTypes.REPORTS_PENDING:
      return {
        ...state,
        reports: {
          ...state.reports,
          data: [],
          loading: true,
        },
      } as ILendingStore;
    case actionTypes.REPORTS_SUCCESS:
      return {
        ...state,
        reports: {
          ...state.reports,
          data: action.payload,
          error: false,
          loading: false,
        },
      } as ILendingStore;
    case actionTypes.REPORTS_ERROR:
      return {
        ...state,
        reports: {
          ...state.reports,
          data: [],
          loading: false,
          error: true,
        },
      } as ILendingStore;
    case actionTypes.GET_AGENT_LOAN_APPLICATIONS_PENDING:
      return {
        ...state,
        agentLoanApplications: { ...state.agentLoanApplications, error: null, loading: true },
      };
    case actionTypes.GET_AGENT_LOAN_APPLICATIONS_SUCCESS:
      return {
        ...state,
        agentLoanApplications: { ...action.payload, error: null, loading: false },
      };
    case actionTypes.GET_AGENT_LOAN_APPLICATIONS_ERROR:
      return {
        ...state,
        agentLoanApplications: { ...state.agentLoanApplications, error: action.payload, loading: false },
      };
    case actionTypes.GET_AIRTABLE_AGENT_LOAN_APPLICATIONS_PENDING:
      return { ...state, airtableAgentLoanApplications: { ...state.airtableAgentLoanApplications, loading: true, error: null } };
    case actionTypes.GET_AIRTABLE_AGENT_LOAN_APPLICATIONS_SUCCESS:
      return { ...state, airtableAgentLoanApplications: { loading: false, data: action.payload, error: null } };
    case actionTypes.GET_AIRTABLE_AGENT_LOAN_APPLICATIONS_ERROR:
      return { ...state, airtableAgentLoanApplications: { ...state.airtableAgentLoanApplications, loading: false, error: action.payload } };
    case actionTypes.CLEAR_AIRTABLE_AGENT_LOAN_APPLICATION:
      return { ...state, airtableAgentLoanApplications: {} as any };
    case actionTypes.CREATE_AIRTABLE_AGENT_LOAN_APPLICATION_PENDING:
    case actionTypes.UPDATE_AIRTABLE_AGENT_LOAN_APPLICATION_PENDING:
      return { ...state, airtableAgentLoanApplications: { ...state.airtableAgentLoanApplications, updateLoading: true, error: null } };
    case actionTypes.CREATE_AIRTABLE_AGENT_LOAN_APPLICATION_SUCCESS:
    case actionTypes.UPDATE_AIRTABLE_AGENT_LOAN_APPLICATION_SUCCESS:
      message.success('Loan application updated');
      return { ...state, airtableAgentLoanApplications: { ...state.airtableAgentLoanApplications, updateLoading: false, error: null } };
    case actionTypes.CREATE_AIRTABLE_AGENT_LOAN_APPLICATION_ERROR:
    case actionTypes.UPDATE_AIRTABLE_AGENT_LOAN_APPLICATION_ERROR:
      message.error('Unable to update loan application');
      return { ...state, airtableAgentLoanApplications: { ...state.airtableAgentLoanApplications, updateLoading: false, error: action.payload } };
    case actionTypes.GET_CREDIT_CHECK_PENDING:
      return { ...state, creditCheck: { ...state.creditCheck, loading: true, error: null } };
    case actionTypes.GET_CREDIT_CHECK_SUCCESS:
      return { ...state, creditCheck: { data: action.payload.data, loading: false, error: null } };
    case actionTypes.GET_CREDIT_CHECK_ERROR:
      return { ...state, creditCheck: { ...state.creditCheck, loading: false, error: action.payload } };
    case actionTypes.CLEAR_CREDIT_CHECK:
      return { ...state, creditCheck: {} as any };
    default:
      return state;
  }
};

export default lendingReducer;
